import * as React from "react";
import { api, postJson } from "./api";
import { AppLink, useNavigation } from "./router";
import { TrackSearch } from "./TrackSearch";
import type { TrackResult } from "./track-search";
import { useRoomSession, type AppUser } from "./room-session";

export function RoomPage({ roomRef, invited, user, onRequireAuth }: {
  roomRef: string;
  invited: boolean;
  user: AppUser | null;
  onRequireAuth: (pendingAction?: string) => void;
}) {
  const { navigate } = useNavigation();
  const session = useRoomSession();
  const [rightTab, setRightTab] = React.useState<"queue" | "chat">("queue");
  const [chat, setChat] = React.useState("");
  const [busy, setBusy] = React.useState("");
  const [notice, setNotice] = React.useState("");
  const [lyrics, setLyrics] = React.useState<{ plain: string | null; synced: string | null } | null>(null);
  const [showLyrics, setShowLyrics] = React.useState(false);
  const [devices, setDevices] = React.useState<Array<{ id: string; name: string; type: string; isActive: boolean; isRestricted: boolean }>>([]);
  const [showDevices, setShowDevices] = React.useState(false);

  React.useEffect(() => {
    void session.joinRoom(roomRef).then((joined) => {
      if (!joined && session.room) navigate(`/rooms/${encodeURIComponent(session.room.alias)}`, { replace: true });
    });
  }, [roomRef]);

  const room = session.room;
  const isHost = Boolean(user && room?.hostHandle === user.handle);
  const isHouseStation = room?.roomKind === "house";

  React.useEffect(() => {
    if (!showLyrics || !room?.nowPlaying) return;
    const params = new URLSearchParams({ title: room.nowPlaying.title, artist: room.nowPlaying.artist });
    void api<{ lyrics: string | null; syncedLyrics: string | null }>(`/api/lyrics?${params}`)
      .then((payload) => setLyrics({ plain: payload.lyrics, synced: payload.syncedLyrics }))
      .catch(() => setLyrics({ plain: null, synced: null }));
  }, [showLyrics, room?.nowPlaying?.spotifyId]);

  const addTrack = async (track: TrackResult) => {
    setBusy(track.spotifyId);
    setNotice("");
    try {
      await postJson(`/api/rooms/${encodeURIComponent(roomRef)}/queue`, { trackId: track.spotifyId });
      setNotice(`Sent “${track.title}” into the room.`);
    } catch (cause: any) {
      setNotice(cause?.message || "Could not add that song");
    } finally { setBusy(""); }
  };

  const sendChat = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!user) return onRequireAuth("chat");
    if (!chat.trim()) return;
    setBusy("chat");
    try {
      await session.roomAction("chat", { body: chat.trim() });
      setChat("");
    } catch (cause: any) { setNotice(cause?.message || "Message failed"); }
    finally { setBusy(""); }
  };

  const control = async (action: string) => {
    setBusy(action);
    try {
      await session.activateBrowserPlayer();
      await session.roomAction(`playback/${action}`, {
        deviceId: session.browserPlayer.deviceId || undefined,
      });
    }
    catch (cause: any) { setNotice(cause?.message || "Playback action failed"); }
    finally { setBusy(""); }
  };

  const loadDevices = async () => {
    if (!user) return onRequireAuth("listen");
    setShowDevices(true);
    try { setDevices((await api<{ devices: typeof devices }>("/api/spotify/devices")).devices); }
    catch (cause: any) { setNotice(cause?.message || "Could not load Spotify devices"); }
  };

  const listenHere = async (deviceId: string) => {
    setBusy(deviceId);
    try {
      await session.listenOnDevice(deviceId);
      setShowDevices(false);
    } catch (cause: any) { setNotice(cause?.message || "Could not transfer playback"); }
    finally { setBusy(""); }
  };

  const stumbleNext = async () => {
    try {
      const payload = await postJson<{ room: { alias?: string; id: string } }>("/api/matchmake", {});
      const next = payload.room.alias || payload.room.id;
      if (await session.joinRoom(next)) navigate(`/rooms/${encodeURIComponent(next)}`);
    } catch (cause: any) { setNotice(cause?.message || "No other rooms found"); }
  };

  if (!room || (session.connection === "connecting" && session.activeRoomRef === roomRef)) {
    return <main className="room-loading page-shell"><div className="signal-loader"><i /><i /><i /></div><h1>Tuning into {roomRef}</h1><p>Opening the live room coordinator…</p>{session.error && <p className="field-error">{session.error}</p>}</main>;
  }

  return (
    <main className="room-page page-shell">
      {invited && <div className="invite-banner"><span>✦</span><strong>You were invited into {room.title}.</strong><button onClick={() => navigate(`/rooms/${room.alias}`, { replace: true })}>Got it</button></div>}
      <header className="room-heading">
        <div><span className="eyebrow"><i className={room.sessionState === "live" ? "live-dot" : ""} /> {isHouseStation ? `HOUSE STATION · ${room.sessionState.toUpperCase()}` : session.connection === "live" ? "LIVE ROOM" : session.connection.toUpperCase()}</span><h1>{room.title}</h1><p>{isHouseStation ? "A permanent Jamband rotation shaped by whoever is listening." : `jamband.io/rooms/${room.alias}`}</p></div>
        <div className="room-heading-actions">
          <button className="button ghost" onClick={() => { void navigator.clipboard.writeText(`${window.location.origin}/invite/${room.alias}`); setNotice("Invite link copied."); }}>Copy invite</button>
          <button className="button chrome" onClick={() => void stumbleNext()}>Stumble next →</button>
        </div>
      </header>
      {(notice || session.error) && <div className="notice" role="status">{notice || session.error}</div>}

      <div className="room-layout">
        <section className="now-playing-panel panel">
          <div className="now-playing-stage">
            <div className="album-frame">
              {room.nowPlaying?.coverUrl ? <img src={room.nowPlaying.coverUrl} width="560" height="560" alt="" /> : <div className="album-placeholder"><span>JB</span><small>WAITING FOR A SONG</small></div>}
              <div className="playback-glow" />
            </div>
            <div className="track-marquee">
              <span className="eyebrow">NOW PLAYING</span>
              <h2>{room.nowPlaying?.title || (isHouseStation ? "Ready when you are" : "The room is quiet")}</h2>
              <p>{room.nowPlaying ? `${room.nowPlaying.artist}${room.nowPlaying.album ? ` · ${room.nowPlaying.album}` : ""}` : isHouseStation ? "Choose a Spotify device to start the house rotation." : "Search below and send in the first Fragment."}</p>
            </div>
          </div>
          <div className="transport-strip">
            {isHost ? <>
              <button className="transport-button" onClick={() => void control(room.nowPlaying?.isPlaying ? "pause" : "play")} disabled={Boolean(busy)}>{room.nowPlaying?.isPlaying ? "Ⅱ" : "▶"}<span>{room.nowPlaying?.isPlaying ? "Pause" : "Play"}</span></button>
              <button className="transport-button" onClick={() => void control("next")} disabled={Boolean(busy)}>≫<span>Next</span></button>
            </> : null}
            <button className="transport-button primary-transport" onClick={() => void loadDevices()}>▣<span>{isHost ? "Devices" : "Listen on Spotify"}</span></button>
            <button className="transport-button" onClick={() => setShowLyrics(!showLyrics)}>≡<span>Lyrics</span></button>
            <span className="host-label">{isHouseStation ? "Playback led by " : "Hosted by "}{room.hostHandle ? <AppLink href={`/u/${room.hostHandle}`}>{room.participants.find((person) => person.handle === room.hostHandle)?.displayName || `@${room.hostHandle}`}</AppLink> : isHouseStation ? "the next listener" : "host"}</span>
          </div>
          {showLyrics && <div className="lyrics-drawer"><header><strong>Lyrics</strong><button onClick={() => setShowLyrics(false)}>×</button></header><pre>{lyrics?.synced || lyrics?.plain || (lyrics ? "Lyrics not found." : "Looking up lyrics…")}</pre></div>}
          <div className="room-search-section"><span className="eyebrow">SEND A SONG FRAGMENT TO THIS ROOM</span><TrackSearch authenticated={Boolean(user)} onRequireAuth={() => onRequireAuth("queue")} onSelect={addTrack} actionLabel={busy ? "Sending…" : "Send"} /></div>
        </section>

        <aside className="room-sidebar panel">
          <div className="people-strip"><header><span>LISTENERS</span><b>{room.participants.length}</b></header><div className="participant-list">{room.participants.length === 0 ? <p>Watching as a guest</p> : room.participants.map((person) => <AppLink href={`/u/${person.handle}`} className="participant" key={person.handle}>{person.avatarUrl ? <img src={person.avatarUrl} alt="" width="34" height="34" /> : <span>{person.displayName.slice(0, 1)}</span>}<span><strong>{person.displayName}</strong><small>@{person.handle}</small></span>{person.role === "host" && <i>HOST</i>}</AppLink>)}</div></div>
          <div className="sidebar-tabs" role="tablist"><button className={rightTab === "queue" ? "active" : ""} onClick={() => setRightTab("queue")}>Queue <b>{room.queue.length}</b></button><button className={rightTab === "chat" ? "active" : ""} onClick={() => setRightTab("chat")}>Chat <b>{room.chat.length}</b></button></div>
          {rightTab === "queue" ? <div className="queue-list">{room.queue.length === 0 ? <div className="empty-rail"><span>＋</span><p>No songs waiting.</p></div> : room.queue.map((item, index) => <div className="queue-item" key={item.id}><span className="queue-number">{index + 1}</span><div className="track-art tiny-art">{item.artworkUrl ? <img src={item.artworkUrl} alt="" width="40" height="40" /> : "♪"}</div><div><strong>{item.title}</strong><small>{item.artist} · by <AppLink href={`/u/${item.addedBy.handle}`}>@{item.addedBy.handle}</AppLink></small></div><button aria-label={`Vote for ${item.title}`} disabled={!user} onClick={() => user ? void postJson(`/api/rooms/${room.alias}/queue/${item.id}/vote`, {}) : onRequireAuth("queue")}>▲ {item.score}</button></div>)}</div> : <div className="chat-panel"><div className="chat-list">{room.chat.map((message) => <div className="chat-message" key={message.id}><AppLink href={`/u/${message.handle}`}>@{message.handle}</AppLink><p>{message.body}</p></div>)}</div><form onSubmit={sendChat}><input value={chat} maxLength={1000} onChange={(event) => setChat(event.target.value)} placeholder={user ? "Say something…" : "Connect Spotify to chat"} disabled={!user} /><button disabled={busy === "chat" || !chat.trim()}>Send</button></form></div>}
        </aside>
      </div>

      {showDevices && <div className="modal-backdrop" onMouseDown={(event) => event.target === event.currentTarget && setShowDevices(false)}><section className="device-dialog" role="dialog" aria-modal="true"><header><div><span className="eyebrow">SPOTIFY CONNECT</span><h2>Listen on a device</h2></div><button className="icon-button" onClick={() => setShowDevices(false)}>×</button></header>{session.browserPlayer.ready && <button className="device-row browser-device" onClick={() => void listenHere(session.browserPlayer.deviceId)}><span>▣</span><div><strong>This browser</strong><small>Jamband Web Playback</small></div><b>{busy === session.browserPlayer.deviceId ? "Connecting…" : "Play here"}</b></button>}{session.browserPlayer.error && <p className="field-error">{session.browserPlayer.error}</p>}{devices.length === 0 && !session.browserPlayer.ready ? <p>Open Spotify on a device, then refresh.</p> : devices.filter((device) => device.id !== session.browserPlayer.deviceId).map((device) => <button className="device-row" disabled={device.isRestricted} key={device.id} onClick={() => void listenHere(device.id)}><span>{device.type === "Computer" ? "▣" : "▯"}</span><div><strong>{device.name}</strong><small>{device.type}{device.isActive ? " · active" : ""}{device.isRestricted ? " · Spotify-restricted" : ""}</small></div><b>{device.isRestricted ? "Unavailable" : busy === device.id ? "Connecting…" : "Play here"}</b></button>)}<button className="button ghost full" onClick={() => void loadDevices()}>Refresh devices</button></section></div>}
    </main>
  );
}
